import { apiClient } from './client';
import type { TaskPriority, TaskStatus } from './tasks';

/**
 * Project Home API client (project-menu-detailing) — /project 대시보드 1콜 집계.
 * backend `apps/chat-server/src/modules/tasks/project-home.routes.ts` 와 1:1.
 * 계약: docs/design/interface-project-menu.md §2.
 */

export interface ProjectHomeSummaryDto {
  teams: { total: number; leaderOf: number };
  projects: {
    total: number;
    byRole: Record<'OWNER' | 'MANAGER' | 'MEMBER' | 'VIEWER', number>;
    active: number;
    archived: number;
  };
  myTasks: {
    total: number;
    byStatus: Record<'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE', number>;
    dueSoon7d: number;
    overdue: number;
    /**
     * AW-005 — '위험'(지연 ＋ 우선순위 HIGH)의 서버 집계.
     *
     * 종전에는 홈이 `/tasks/me` 상위 20건에서 파생해, 전체 집계인 dueSoon7d·overdue 와
     * 같은 줄에 서면서도 세는 모집단이 달랐다. 구버전 서버 응답에는 없어 0 으로 정규화된다.
     *
     * ⭐ 2026-10-10 — ★ 우리 서버는 ⭐ «이미 보낸다».
     *   ⇒ 실측 ⇒ `git grep -c riskCount -- apps/chat-server/src`
     *      ⇒ ⇒ modules/tasks/project-home.routes.ts ⇒ ★ 4 · 그 시험 ⇒ ★ 11
     *   ⇒ ⇒ ⚠ 그래서 ★ 위 「구버전 서버 응답에는 없어」는 ⭐ «보험»이다 — ⇒ 늘 가는 길이 ⛔ 아니다
     *      ⇒ ⇒ ★ 그 줄만 읽으면 ⭐ 「서버가 안 보낸다」로 읽힌다 ⇒ ⇒ 그래서 이 줄을 붙였다
     */
    riskCount: number;
  };
  weeklyDoneRate: number | null;
  /**
   * AW-005 — 프로젝트별 지연 건수(내 할일 기준). 스페이스·프로젝트 카드의 '지연 n건' 칩이
   * 히어로 숫자와 같은 모집단을 쓰게 하는 값. 팀 단위 합계는 프론트가 projects 로 롤업한다.
   *
   * ⭐ 2026-10-10 — ★ 이 칸도 ⭐ 서버가 «이미 보낸다».
   *   ⇒ 실측 ⇒ `git grep -c overdueByProject -- apps/chat-server/src`
   *      ⇒ ⇒ modules/tasks/project-home.routes.ts ⇒ ★ 3 · 그 시험 ⇒ ★ 10
   *   ⇒ ⚠ 그러니 ★ 쓰는 쪽(ProjectHomeSpaces.tsx)의 ⭐ 「구버전 서버 → 폴백」 갈래는
   *      ⇒ ⇒ ★ «혹시 모를 때»의 보험이다 ⇒ ⇒ 그 갈래가 돈다고 ⛔ 서버 결함이 아니다
   */
  overdueByProject: { projectId: string; count: number }[];
}

// v2.14.2 — 200 인데 형태가 깨진 응답(레이트리밋 변형·프록시 이상)이
// 소비처의 summary.myTasks.byStatus 등 중첩 접근을 ErrorBoundary 로 죽이는 것 방어.
// 단일 지점 정규화로 모든 소비처(ProjectStatsCards·StatsDetailPage·MyTasksDetailPage) 보호.
function normalizeSummary(d: Partial<ProjectHomeSummaryDto> | null | undefined): ProjectHomeSummaryDto {
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const t = d?.teams ?? {} as Partial<ProjectHomeSummaryDto['teams']>;
  const p = d?.projects ?? {} as Partial<ProjectHomeSummaryDto['projects']>;
  const m = d?.myTasks ?? {} as Partial<ProjectHomeSummaryDto['myTasks']>;
  const bs = m.byStatus ?? {} as Partial<ProjectHomeSummaryDto['myTasks']['byStatus']>;
  const br = p.byRole ?? {} as Partial<ProjectHomeSummaryDto['projects']['byRole']>;
  return {
    teams: { total: num(t.total), leaderOf: num(t.leaderOf) },
    projects: {
      total: num(p.total),
      byRole: { OWNER: num(br.OWNER), MANAGER: num(br.MANAGER), MEMBER: num(br.MEMBER), VIEWER: num(br.VIEWER) },
      active: num(p.active),
      archived: num(p.archived),
    },
    myTasks: {
      total: num(m.total),
      byStatus: { TODO: num(bs.TODO), IN_PROGRESS: num(bs.IN_PROGRESS), REVIEW: num(bs.REVIEW), DONE: num(bs.DONE) },
      dueSoon7d: num(m.dueSoon7d),
      overdue: num(m.overdue),
      riskCount: num(m.riskCount),
    },
    weeklyDoneRate: typeof d?.weeklyDoneRate === 'number' ? d.weeklyDoneRate : null,
    overdueByProject: Array.isArray(d?.overdueByProject)
      ? d!.overdueByProject
        .filter((r): r is { projectId: string; count: number } => !!r && typeof r.projectId === 'string')
        .map((r) => ({ projectId: r.projectId, count: num(r.count) }))
      : [],
  };
}

export async function getProjectHomeSummary(): Promise<ProjectHomeSummaryDto> {
  const res = await apiClient.get<Partial<ProjectHomeSummaryDto>>('/project-home/summary');
  return normalizeSummary(res.data);
}

/* ============================================================================
   프로젝트 통합 검색 (AW-005) — 2026-10-07 에 «부르는 쪽»을 세웠다.

   ⛔ 서버는 2026-08-25 에 섰는데(project-home-search.routes.ts · 시험 10건)
      ★ 프론트에서 부르는 곳이 «0건»이었다. 그동안 홈의 검색창은 이미 받아 둔 것을 걸렀고,
      그래서 ⇒ 찾는 태스크가 /tasks/me 상위 20건 밖이면 ★ 걸리지도 않았다.

   ⚠ 칸 이름을 ★ 손으로 적지 않는다 — 서버가 칸을 더하면 조용히 어긋난다.
     ⇒ project-search-contract.test.ts 가 ★ 라우트 파일을 «읽어» 이 아래 타입과 맞댄다.
   ============================================================================ */

/** 스페이스(팀) — 서버 `spaces[]`. */
export interface ProjectSearchSpaceDto {
  id: string;
  name: string;
  color: string | null;
  memberCount: number;
  projectCount: number;
}

/** 프로젝트 — 서버 `projects[]`. teamName 은 soft-delete 된 팀이면 null 이다. */
export interface ProjectSearchProjectDto {
  id: string;
  name: string;
  color: string | null;
  teamId: string | null;
  teamName: string | null;
}

/** 할일 — 서버 `tasks[]`. ⚠ `tags` 는 «없다»(그 응답이 안 보낸다) — MyTaskDto 를 재사용하지 않는 까닭. */
export interface ProjectSearchTaskDto {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  project: { id: string; name: string; teamId: string | null; teamName: string | null };
}

export interface ProjectSearchResultDto {
  /**
   * ⭐ **묶음마다 ★ 「상한에서 잘렸나」** (서버 2026-10-07 · 프론트 2026-10-08).
   *
   * ```
   * 서버 project-home-search.routes.ts:177-179
   *   spacesHasMore: teamRows.length > limit   (take: limit + 1 로 ★ 하나 더 떠서 «넘치나»만 본다)
   *   ⇒ ⭐ 새 질의 0 · count 0 — 「몇 건 더」가 아니라 ★ 「더 있나」만 안다
   * ```
   * ⛔ **`total` 은 «없다»** — 그래서 ⭐ **「N건 더 있음」의 N 을 적을 수 없다.**
   *   ⇒ ★ 「더 있습니다」 꼴로만 말한다. ⇒ N 이 필요해지면 **서버에 count 질의 셋**이 더 든다.
   * ⚠ 이름이 **camelCase** 다 — 이 응답의 기존 칸(`memberCount`·`teamId`·`dueDate`)과 **같은 꼴**이다.
   * ⚠ 빈 질의에서도 서버가 **세 칸을 `false` 로 준다**(`:59`) ⇒ ★ 칸이 **늘** 있다.
   */
  spacesHasMore: boolean;
  projectsHasMore: boolean;
  tasksHasMore: boolean;
  spaces: ProjectSearchSpaceDto[];
  projects: ProjectSearchProjectDto[];
  tasks: ProjectSearchTaskDto[];
}

/**
 * ⛔ **깨진 응답을 «해당 없음»으로 바꾸지 않는다** — 위 `normalizeSummary` 와 ★ 반대로 간다.
 *
 * ```
 * summary 는 숫자라 0 으로 메워도 「0건」이 틀린 말이 아니다.
 * ⛔ 그런데 검색은 ★ 「0건」과 「모른다」가 «다른 말»이다 —
 *    빈 배열로 메우면 ⇒ 응답이 깨졌는데 화면이 ★ 「해당 없음」이라고 ⭐ 거짓말을 한다.
 * ⇒ ★ 그래서 던진다. 화면은 그 오류를 ⭐ 「다시 시도」와 함께 보여 준다.
 * ```
 */
function assertSearchResult(d: unknown): ProjectSearchResultDto {
  const o = d as Partial<ProjectSearchResultDto> | null | undefined;
  if (!Array.isArray(o?.spaces) || !Array.isArray(o?.projects) || !Array.isArray(o?.tasks)) {
    throw new Error('project-home/search 응답이 세 묶음(spaces·projects·tasks)을 다 주지 않았다');
  }
  /*
   * ⚠⚠ 2026-10-08 — ★ 여기서 ⭐ «칸이 사라지고 있었다».
   *   종전: `return { spaces: o.spaces, projects: o.projects, tasks: o.tasks }`
   *   ⇒ ⛔ 서버가 `*HasMore` 를 보내기 시작해도 ★ 이 줄이 «골라 담아» 버렸다.
   *   ⇒ ⇒ ★ 「서버가 준다」와 ⭐ 「화면이 받는다」는 ★ 다른 수다 — 그 사이에 이 줄이 있다.
   *
   * ⚠ 그리고 ★ 「모른다」와 「아니다」를 가른다: 옛 서버(그 칸이 «없는» 판)가 답하면
   *   ⇒ `undefined` 다. ⇒ ⭐ 그때는 ★ `false` 로 본다 — 「더 있다」고 ⛔ 거짓말하지 않는다.
   *   ⇒ ⚠ 위 배열과 달리 **던지지 않는다**: 묶음이 없으면 화면을 못 그리지만,
   *     이 칸이 없으면 ★ 「말을 덜 할」 뿐이고 ⇒ ⭐ 그것은 «깨진 응답»이 아니다.
   */
  const flag = (v: unknown): boolean => v === true;
  return {
    spacesHasMore: flag(o.spacesHasMore),
    projectsHasMore: flag(o.projectsHasMore),
    tasksHasMore: flag(o.tasksHasMore),
    spaces: o.spaces,
    projects: o.projects,
    tasks: o.tasks,
  };
}

/**
 * ⚠ **빈 질의는 ★ 부르지 않는다.** 서버 `:54` 도 빈 q 에 빈 묶음 셋을 200 으로 주지만,
 *   ⇒ ★ 그 왕복은 ⭐ 아무것도 알려 주지 않는다. ⇒ 부르는 쪽에서 막는다.
 *   ⇒ ⇒ 그래서 이 함수는 ★ 빈 q 에 `null` 을 준다 — 「0건」이 «아니라» ⭐ 「안 물었다」다.
 */
export async function searchProjectHome(q: string, limit?: number): Promise<ProjectSearchResultDto | null> {
  const trimmed = q.trim();
  if (trimmed.length === 0) return null;
  const res = await apiClient.get<unknown>('/project-home/search', {
    params: { q: trimmed, ...(limit === undefined ? {} : { limit }) },
  });
  return assertSearchResult(res.data);
}
